import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	Signal,
	ViewChild,
	WritableSignal,
	computed,
	effect,
	signal,
	OnInit
} from "@angular/core";
import { NavController, Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import * as _ from "lodash-es";
import { Subscription, combineLatest, of, interval, Subject, BehaviorSubject } from "rxjs";
import { distinctUntilChanged, skipWhile, switchMap, take, takeUntil } from "rxjs/operators";
import { GetHeaderTitle, ResetHeaderState } from "src/app/shared/actions/utility.actions";
import { TypeTracking } from "src/app/shared/enums/type-analytics";
import { TypeCustomFields } from "src/app/shared/enums/type-custom-fields";
import { TypeModule } from "src/app/shared/enums/type-module";
import { IDocument, IEvent, IEventUser, IFullCustomField, IGroup, IModule, ISheet } from "src/app/shared/interfaces";
import { IFilteredItemFormat } from "src/app/shared/interfaces/custom-fields.interfaces";
import { IFavoriteFolder } from "src/app/shared/interfaces/folders.interfaces";
import { IModuleUpdatedSettings } from "src/app/shared/interfaces/modules.interfaces";
import { getCurrentEventUser } from "src/app/shared/selectors/auth.selectors";
import { getCurrentEvent } from "src/app/shared/selectors/events.selectors";
import {
	getBaseCustomFields,
	getFavoritesFolders,
	getGroupsByOrder,
	getModulesCustomsFieldsOfModule
} from "src/app/shared/selectors/generics-modules-data.selectors";
import { getModulesByType, getSpecificModule } from "src/app/shared/selectors/modules.selectors";
import { selectRouteNestedParams, selectUrl } from "src/app/shared/selectors/router.selectors";
import { CustomFieldsService, SheetsService, UtilityService } from "src/app/shared/services";
import { AnalyticsService } from "src/app/shared/services/analytics.service";
import { environment } from "src/environments/environment";
import { ISearchFilter } from "src/app/shared/interfaces/search.interfaces";
import { buildFiltersQuery, filterSearch } from "src/app/shared/helpers-functions/filter-search";
import { IFilters } from "src/app/shared/interfaces/filters.interfaces";
import { DateTime } from "luxon";
import { getDocuments } from "src/app/shared/selectors/documents.selectors";

@Component({
	selector: "app-sheets",
	templateUrl: "./sheets.component.html",
	styleUrls: ["./sheets.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class SheetsComponent implements OnDestroy, OnInit {
	@ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;

	destroySuscription: Subject<any> = new Subject();
	subscriptions: Subscription[] = [];
	searchSub: Subscription;
	eventUserSub: Subscription;
	analyticsArraySub: { name: string; sub: Subscription }[] = [];

	loader: boolean = true;
	limit: number = 20;
	p: number = 0;

	searchOpen: boolean = false;
	searchText: string = "";

	totalDatas: number = 0;

	eventId: string;
	event: IEvent;
	module: IModule;
	moduleId: string;
	moduleUpdatedSettings: IModuleUpdatedSettings;
	eventUser: IEventUser;
	allDatas: WritableSignal<ISheet[]> = signal([]);
	datasFiltered: Signal<ISheet[]> = computed(() => {
		const allDatas = this.allDatas();
		if (this.datasGetType === "firestore") {
			const filtersQuery = this.filtersQuery();
			const datasFiltered = filterSearch(filtersQuery, allDatas);
			return datasFiltered.datas;
		} else {
			return this.allDatas();
		}
	});

	datasFilteredInTwo: Signal<ISheet[][]> = computed(() => {
		let count = 0;
		let index = 0;
		const newFilteredDatasOnTwo = [];
		this.datasFiltered().forEach((data) => {
			if (count === 2) {
				index++;
				count = 0;
			}
			if (!newFilteredDatasOnTwo[index]) {
				newFilteredDatasOnTwo[index] = [];
			}
			newFilteredDatasOnTwo[index].push(data);
			count++;
		});
		return newFilteredDatasOnTwo;
	});
	groups: IGroup[] = [];
	documents: IDocument[] = [];
	customFields: IFullCustomField[] = [];
	tagCustomFields: IFullCustomField[] = [];

	// filter properties
	filterItems: IFilteredItemFormat[];

	typeOrder: string;

	showNoResultImage: boolean = false;

	allowFilterTag: boolean = false; // To show or hide filter tag
	isMobile = window.innerWidth < 768 ? true : false;
	selectedFilters: IFilteredItemFormat[] = [];

	groupedLetters: any;
	itemSize: number = 97;
	height: number = 873;

	dontBelongAnyGroups: boolean = false;

	init: boolean = false;
	navigationSaved: { moduleId: string; lastIndex: number }[] = [];
	currentLanguage: WritableSignal<string> = signal(environment.platform.defaultLanguage);
	searchValue: string = "";
	searchValueUpdated: Subject<string> = new Subject<string>();

	filters: WritableSignal<IFilters> = signal({
		locations: [],
		tracks: [],
		groups: [],
		customFields: [],
		principalKey: ""
	});
	filtersLoader: boolean = true;

	filtersQuery: WritableSignal<ISearchFilter> = signal({
		equalityFields: [],
		inequalityFields: [],
		includeTextFields: [],
		includeOrTextFields: [],
		superiorFields: [],
		superiorOrEqualFields: [],
		inferiorFields: [],
		inferiorOrEqualFields: [],
		anyTextFields: [],
		arrayContainsAnyFields: [],
		arrayContainsAllFields: [],
		arrayContainsBlocAndOrFields: [],
		arrayNotContainsFields: [],
		elemMatchArrayFields: [],
		page: this.p,
		itemsPerPage: this.limit,
		sortBy: []
	});

	favoriteModule: IModule;
	favoriteFolder: IFavoriteFolder;
	languageSub: Subscription;

	initDatasSubject: BehaviorSubject<{
		initEvent: boolean;
		initModule: boolean;
		initEventUser: boolean;
		initDatas: boolean;
		initFavorites: boolean;
		initGroups: boolean;
		initDocuments: boolean;
		initCustomFields: boolean;
		initFilters: boolean;
	}> = new BehaviorSubject(null);
	initDatasChecks: {
		initEvent: boolean;
		initModule: boolean;
		initEventUser: boolean;
		initDatas: boolean;
		initFavorites: boolean;
		initGroups: boolean;
		initDocuments: boolean;
		initCustomFields: boolean;
		initFilters: boolean;
	} = {
		initEvent: false,
		initModule: false,
		initEventUser: false,
		initDatas: false,
		initFavorites: false,
		initGroups: false,
		initDocuments: false,
		initCustomFields: false,
		initFilters: false
	};

	firstTimeModuleAccess: boolean = true;

	filtersUuid: string = this.SUtility.generateUuid();
	mainLoader: boolean = true;

	datasGetType: "mongo" | "firestore" = "firestore";

	constructor(
		private navCtrl: NavController,
		public platform: Platform,
		private SAnalytics: AnalyticsService,
		private store: Store,
		private SCustomFields: CustomFieldsService,
		private SSheets: SheetsService,
		private STranslate: TranslateService,
		private SUtility: UtilityService,
		private cdr: ChangeDetectorRef
	) {
		this.searchSub = this.searchValueUpdated.pipe(distinctUntilChanged()).subscribe((evtValue) => {
			this.loader = true;
			this.resetPagination();
			if (evtValue.length >= 1) {
				this.searchValue = evtValue;
				this.buildFilterQuery();
			} else {
				this.resetFilter();
			}
		});

		effect(() => {
			const computeReachableDatas = this.SUtility.computeReachableDatas(
				this.event,
				this.allDatas(),
				this.datasFiltered(),
				this.customFields,
				this.filters(),
				this.currentLanguage(),
				this.groups,
				[],
				[],
				this.datasGetType
			);
			this.filters.set(computeReachableDatas.filters);
			this.groupedLetters = this.datasFiltered().reduce((groups, data) => {
				const letter = data.queryName.charAt(0);
				if (!Object.values(groups).includes(letter)) {
					const uid = data.uid;
					groups[uid] = letter;
				}
				return groups;
			}, {});

			if (this.init) {
				this.loader = false;
			}

			this.loader = false;

			this.cdr.markForCheck();
		});
	}

	ngOnInit() {
		this.currentLanguage.set(this.STranslate.currentLang);
		this.STranslate.onLangChange.pipe(takeUntil(this.destroySuscription)).subscribe((lang) => {
			this.currentLanguage.set(lang.lang);
			this.getFilters();
		});
	}

	ionViewWillEnter() {
		this.initDatasSubject
			.pipe(
				skipWhile(
					(initState) =>
						!initState ||
						!initState.initEvent ||
						!initState.initModule ||
						!initState.initEventUser ||
						!initState.initDatas ||
						!initState.initCustomFields ||
						!initState.initGroups ||
						!initState.initFilters
				),
				take(1)
			)
			.subscribe(() => {
				this.mainLoader = false;
				this.init = true;
			});

		this.store
			.select(selectUrl)
			.pipe(take(1))
			.subscribe(() => {
				this.store
					.select(selectRouteNestedParams)
					.pipe(take(1))
					.subscribe((params) => {
						this.eventId = params.eventId;
						this.init = false;

						const checkNeedReload = this.checkNeedReloadDatas(params.moduleId);

						this.moduleId = params.moduleId;

						// Analytics
						this.SAnalytics.moduleAccess(this.eventId, this.moduleId, TypeTracking.ACCESS_TO_SHEETS_MODULE);

						if (checkNeedReload) {
							this.firstTimeModuleAccess = true;

							this.unloadDatas();
							this.initDatas();
						} else {
							if (this.module) {
								this.store.dispatch(GetHeaderTitle({ payload: this.module.name }));
							}
							this.getEventUser();
						}
					});
			});
	}

	ionViewDidEnter() {
		interval(100)
			.pipe(
				skipWhile(() => this.loader || !this.virtualScroll),
				take(1)
			)
			.subscribe(() => {
				if (this.virtualScroll) {
					const nav = this.navigationSaved.find((navMod) => navMod.moduleId === this.moduleId);
					this.virtualScroll.scrollToIndex(nav ? nav.lastIndex : 0, "auto");
				}
			});
	}

	ionViewWillLeave() {
		// this.allDatas.set([]);
		// this.subscriptions.forEach((sub) => sub.unsubscribe());
		this.store.dispatch(ResetHeaderState(null));
		// this.loader = true;
		// this.mainLoader = true;
		// this.init = false;
		// this.initDatasChecks = {
		// 	initEvent: false,
		// 	initModule: false,
		// 	initEventUser: false,
		// 	initDatas: false,
		// 	initFavorites: false,
		// 	initGroups: false,
		// 	initCustomFields: false,
		// 	initFilters: false
		// };
		// this.initDatasSubject.next(this.initDatasChecks);
		// this.resetFilter();
	}

	ngOnDestroy() {
		this.subscriptions.concat([this.eventUserSub, this.searchSub]).forEach((sub) => sub.unsubscribe());
		this.analyticsArraySub.forEach((analyticData) => analyticData.sub.unsubscribe());
		this.destroySuscription.complete();
	}

	initDatas() {
		this.getEvent();
		this.getModule();
		this.getEventUser();
		this.getGroups();
		this.getAllDocuments();
		this.getCustomFields();
		this.getFilters();
		this.buildFilterQuery();
		this.getFavoriteModuleAndFolders();
	}

	checkNeedReloadDatas(moduleId: string) {
		let needReloadDatas: boolean = false;
		if (this.firstTimeModuleAccess || !this.moduleId || (this.moduleId && this.moduleId !== moduleId)) {
			needReloadDatas = true;
		}
		return needReloadDatas;
	}

	/**
	 * unloadDatas
	 */
	unloadDatas() {
		this.subscriptions.forEach((sub) => sub?.unsubscribe());
		this.analyticsArraySub.forEach((analyticData) => analyticData.sub.unsubscribe());

		this.initDatasChecks = {
			initEvent: false,
			initModule: false,
			initEventUser: false,
			initDatas: false,
			initFavorites: false,
			initGroups: false,
			initDocuments: false,
			initCustomFields: false,
			initFilters: false
		};
		this.mainLoader = true;
		this.filtersLoader = true;
		this.init = false;
		this.event = null;
		this.module = null;
		this.eventUser = null;
		this.favoriteFolder = null;
		this.favoriteModule = null;
		this.p = 0;
		this.searchValue = "";
		this.allDatas.set([]);
		this.firstTimeModuleAccess = true;
		this.initDatasSubject.next(this.initDatasChecks);
		// this.resetFilter();
	}

	/**
	 * Get event
	 */
	getEvent() {
		this.subscriptions.push(
			this.store.select(getCurrentEvent).subscribe((event) => {
				this.event = event;

				if (this.event) {
					if (event && event.limitations && event.limitations.usersLimit <= 1000) {
						// If event has less or equal than 1000 users
						this.datasGetType = "firestore";
					} else {
						// If event has more than 1000 users
						this.datasGetType = "mongo";
					}

					this.initDatasChecks.initEvent = true;
					this.initDatasSubject.next(this.initDatasChecks);
				}
			})
		);
	}

	getModule() {
		this.subscriptions.push(
			this.store.select(getSpecificModule(this.moduleId)).subscribe((module) => {
				if (!_.isEqual(this.module, module)) {
					this.module = module;

					if (this.firstTimeModuleAccess) this.resetQueryFilters();

					this.initDatasChecks.initModule = true;
					this.initDatasSubject.next(this.initDatasChecks);
				}

				this.store.dispatch(
					GetHeaderTitle({ payload: this.module && this.module.name ? this.module.name : null })
				);
			})
		);
	}

	getEventUser() {
		if (this.eventUserSub && !this.eventUserSub.closed) {
			this.eventUserSub.unsubscribe();
		}

		this.eventUserSub = this.store.select(getCurrentEventUser).subscribe((eventUser) => {
			if (!_.isEqual(this.eventUser, eventUser)) {
				this.eventUser = eventUser;
			}
			this.initDatasChecks.initEventUser = true;
			this.initDatasSubject.next(this.initDatasChecks);
			this.cdr.markForCheck();
		});
	}

	/**
	 * getAllDocuments
	 */
	getAllDocuments() {
		this.store
			.select(getDocuments)
			.pipe(take(1))
			.subscribe((documents) => {
				this.documents = documents;
				this.initDatasChecks.initDocuments = true;
				this.initDatasSubject.next(this.initDatasChecks);
			});
	}

	/**
	 * Get groups
	 */
	getGroups() {
		this.store
			.select(getGroupsByOrder("asc"))
			.pipe(take(1))
			.subscribe((groups) => {
				if (!_.isEqual(this.groups, groups)) {
					this.groups = groups;
				}
				this.initDatasChecks.initGroups = true;
				this.initDatasSubject.next(this.initDatasChecks);
			});
	}

	/**
	 * Get custom fields
	 */
	getCustomFields() {
		combineLatest([
			this.store.select(getBaseCustomFields),
			this.store.select(getModulesCustomsFieldsOfModule(this.moduleId))
		])
			.pipe(take(1))
			.subscribe((results) => {
				const baseCustomFields = results[0];
				const modulesCustomFields = results[1];
				this.customFields =
					modulesCustomFields.length > 0
						? modulesCustomFields.map((customField) => {
								const baseCustomFieldCorresponding = baseCustomFields.find(
									(custField) => custField.uid === customField.uid
								);
								return {
									baseSettings: baseCustomFieldCorresponding ? baseCustomFieldCorresponding : null,
									moduleSettings: customField,
									fieldDatas: {
										uid: "",
										field: {
											date: {
												day: 0,
												month: 0,
												year: 0,
												value: "",
												fullDateISO: "",
												zone: ""
											},
											file: {
												lastModifiedDate: "",
												name: "",
												type: "",
												url: "",
												size: 0
											},
											image: {
												format: "",
												lastModifiedDate: "",
												name: "",
												url: "",
												size: 0
											},
											module: {
												items: [],
												moduleType: -1
											},
											multiLanguageText: {
												ArAR: "",
												DeDE: "",
												EnUS: "",
												EsES: "",
												FrFR: "",
												PtBR: ""
											},
											multiLanguageSelectArray: [],
											multiLanguageTextArray: {
												ArAR: [],
												DeDE: [],
												EnUS: [],
												EsES: [],
												FrFR: [],
												PtBR: []
											},
											numeric: 0,
											phoneNumber: {
												countryCode: "",
												dialCode: "",
												e164Number: "",
												internationalNumber: "",
												nationalNumber: "",
												number: ""
											},
											text: ""
										}
									}
								};
							})
						: [];
				this.tagCustomFields = this.customFields.filter((custom) => custom.moduleSettings);

				this.initDatasChecks.initCustomFields = true;
				this.initDatasSubject.next(this.initDatasChecks);
			});
	}

	/**
	 * getFavoriteModuleAndFolders
	 */
	getFavoriteModuleAndFolders() {
		this.subscriptions.push(
			combineLatest([
				this.store.select(getModulesByType(TypeModule.FAVORITES)),
				this.store.select(getFavoritesFolders)
			])
				.pipe(
					skipWhile(() => !this.module || this.module === null),
					take(1)
				)
				.subscribe({
					next: ([modules, favoriteFolders]) => {
						if (modules && modules.length > 0) {
							this.favoriteModule = modules[0];
						}

						if (favoriteFolders && favoriteFolders.length > 0) {
							this.favoriteFolder = favoriteFolders.filter(
								(folder) => this.module && folder.moduleLinkedId === this.module.uid && !folder.hidden
							)?.[0];
						}
					}
				})
		);
	}

	getFilters() {
		this.initDatasSubject
			.pipe(
				skipWhile(
					(initState) =>
						!initState.initEvent ||
						!initState.initModule ||
						!initState.initCustomFields ||
						!initState.initGroups ||
						!initState.initDatas
				),
				take(1),
				switchMap(() => {
					if (
						this.module &&
						this.module.options &&
						(this.module.options.showFilter || this.module.options.showSearchIcon)
					) {
						return this.SCustomFields.getFiltersForModule(
							this.module,
							this.eventUser,
							this.customFields,
							[],
							[],
							this.groups.map((group) => {
								return { name: group.name, uid: group.uid };
							}),
							this.currentLanguage(),
							this.allDatas(),
							this.datasGetType
						);
					} else {
						return of(null);
					}
				})
			)
			.subscribe({
				next: (filters: IFilters) => {
					if (filters) {
						this.integratePreviousFiltersState(filters);
					}

					this.initDatasChecks.initFilters = true;
					this.initDatasSubject.next(this.initDatasChecks);

					this.filtersLoader = false;
				},
				error: (_error) => {
					this.filtersLoader = false;
				}
			});
	}

	integratePreviousFiltersState(filters: IFilters) {
		const newFilters: IFilters = _.cloneDeep(filters);

		this.filters().locations.forEach((filter) => {
			const checkFilter = newFilters.locations.find((specFilter) => specFilter.uid === filter.uid);
			if (checkFilter) {
				checkFilter.checked = filter.checked;
				checkFilter.isReachable = filter.isReachable;
			}
		});
		this.filters().tracks.forEach((filter) => {
			const checkFilter = newFilters.tracks.find((specFilter) => specFilter.uid === filter.uid);
			if (checkFilter) {
				checkFilter.checked = filter.checked;
				checkFilter.isReachable = filter.isReachable;
			}
		});
		this.filters().groups.forEach((filter) => {
			const checkFilter = newFilters.groups.find((specFilter) => specFilter.uid === filter.uid);
			if (checkFilter) {
				checkFilter.checked = filter.checked;
				checkFilter.isReachable = filter.isReachable;
			}
		});
		this.filters().customFields.forEach((filter) => {
			const checkFilter = newFilters.customFields.find((specFilter) => specFilter.uid === filter.uid);
			if (checkFilter) {
				filter.values.forEach((value) => {
					const checkFilterValue = checkFilter.values.find((val) => val.value === value.value);
					if (checkFilterValue) {
						checkFilterValue.isSelected = value.isSelected;
						checkFilterValue.isReachable = value.isReachable;
					}
				});
			}
		});
		this.filters.set(newFilters);
	}

	/**
	 * Get datas
	 */
	getDatas() {
		this.initDatasSubject
			.pipe(
				skipWhile(
					(initState) =>
						!initState.initEvent ||
						!initState.initModule ||
						!initState.initEventUser ||
						!initState.initCustomFields ||
						!initState.initGroups ||
						!initState.initDocuments
					// || !initState.initFilters
				),
				take(1),
				switchMap((_init) => {
					return this.SSheets.getDatasOfModuleForEvent(
						this.moduleId,
						this.datasGetType === "firestore" ? null : this.filtersQuery(),
						"aggregation",
						this.module.typeOrder
					).pipe(take(1));
				})
			)
			.subscribe({
				next: (res) => {
					if (this.datasGetType === "firestore" || (this.datasGetType === "mongo" && this.p === 0)) {
						this.allDatas.set(res.docs);
					} else {
						this.allDatas.set(this.allDatas().concat(res.docs));
					}
					this.totalDatas = res.totalDocuments;

					this.onLoadingMore = false;
					this.firstTimeModuleAccess = false;
					this.initDatasChecks.initDatas = true;
					this.initDatasSubject.next(this.initDatasChecks);
				}
			});
	}

	/**
	 * canShowFilterBloc
	 * @description return true if the filter is at least one custom field that can be filtered
	 * @returns boolean
	 */

	canShowFilterBloc() {
		return (
			this.customFields.some((cus) => cus.moduleSettings.enableFilter) ||
			(this.module.options.requiredFields.group.filter && this.groups.length > 0)
		);
	}

	/**
	 * Filter datas
	 */
	buildFilterQuery() {
		this.initDatasSubject
			.pipe(
				skipWhile(
					(initState) =>
						!initState.initEvent ||
						!initState.initModule ||
						!initState.initEventUser ||
						!initState.initCustomFields ||
						!initState.initGroups
				),
				take(1)
			)
			.subscribe(() => {
				this.loader = true;
				this.resetQueryFilters();
				let filtersQuery = this.filtersQuery();

				filtersQuery.page = this.p;
				filtersQuery.itemsPerPage = this.datasGetType === "firestore" ? 999999 : this.limit;

				if (this.filters() && this.filters().customFields) {
					this.filters().customFields.forEach((filter) => {
						const cus = this.customFields.find((cus) => cus.baseSettings.uid === filter.uid).moduleSettings;
						const fieldKey =
							this.getCustomFieldType(cus.uid) === TypeCustomFields.TEXT ||
							this.getCustomFieldType(cus.uid) === TypeCustomFields.SELECT
								? "multiLanguageText"
								: this.getCustomFieldType(cus.uid) === TypeCustomFields.URL ||
									  this.getCustomFieldType(cus.uid) === TypeCustomFields.EMAIL
									? "text"
									: this.getCustomFieldType(cus.uid) === TypeCustomFields.NUMERIC
										? "numeric"
										: this.getCustomFieldType(cus.uid) === TypeCustomFields.MULTI_SELECT
											? "multiLanguageSelectArray"
											: false;
						const isMultilanguage: boolean =
							this.getCustomFieldType(cus.uid) === TypeCustomFields.TEXT ||
							this.getCustomFieldType(cus.uid) === TypeCustomFields.SELECT ||
							this.getCustomFieldType(cus.uid) === TypeCustomFields.MULTI_SELECT
								? true
								: false;

						if (
							this.datasGetType === "mongo" &&
							this.getCustomFieldType(cus.uid) === TypeCustomFields.TEXT
						) {
							if (filter.search) {
								const bloc = filtersQuery.includeOrTextFields.find(
									(bloc) =>
										bloc.fieldKey ===
										"customFields|" +
											fieldKey +
											"|" +
											cus.uid +
											(isMultilanguage ? "|" + this.currentLanguage() : "")
								);
								if (!bloc) {
									filtersQuery.includeOrTextFields.push({
										fieldKey:
											"customFields|" +
											fieldKey +
											"|" +
											cus.uid +
											(isMultilanguage ? "|" + this.currentLanguage() : ""),
										compareData: [filter.search]
									});
								} else {
									bloc.compareData.push(filter.search);
								}
							}
						} else {
							filter.values.forEach((value) => {
								if (value.isSelected) {
									const bloc = filtersQuery.arrayContainsBlocAndOrFields.find(
										(bloc) =>
											bloc.fieldKey ===
											"customFields|" +
												fieldKey +
												"|" +
												value.filterId +
												(isMultilanguage ? "|" + this.currentLanguage() : "")
									);
									if (!bloc) {
										filtersQuery.arrayContainsBlocAndOrFields.push({
											fieldKey:
												"customFields|" +
												fieldKey +
												"|" +
												value.filterId +
												(isMultilanguage ? "|" + this.currentLanguage() : ""),
											compareData: [value.isNotSpecified ? "" : value.value]
										});
									} else {
										bloc.compareData.push(value.isNotSpecified ? "" : value.value);
									}
								}
							});
						}
					});
				}

				if (this.filters() && this.filters().groups) {
					this.filters().groups.forEach((filter) => {
						if (filter.checked) {
							const bloc = filtersQuery.arrayContainsBlocAndOrFields.find(
								(bloc) => bloc.fieldKey === "groups"
							);
							if (!bloc) {
								filtersQuery.arrayContainsBlocAndOrFields.push({
									fieldKey: "groups",
									compareData: [filter.uid]
								});
							} else {
								bloc.compareData.push(filter.uid);
							}
						}
					});
				}

				if (
					this.module &&
					this.module.options &&
					this.module.options.viewOnlyGroupsContent &&
					this.eventUser &&
					this.eventUser.groups.length > 0
				) {
					this.eventUser.groups.forEach((groupId) => {
						filtersQuery.arrayContainsAnyFields.push({
							fieldKey: "groups",
							compareData: groupId
						});
					});
					if (this.eventUser?.groups.length === 0 && this.module.options.viewOnlyGroupsContent) {
						this.dontBelongAnyGroups = true;
					}
				}

				if (this.module.options.showOnlyLoggedUsers) {
					filtersQuery.equalityFields.push({
						fieldKey: "firstAccess",
						compareData: true
					});
				}

				filtersQuery.equalityFields.push({
					fieldKey: "visibility",
					compareData: true
				});
				// Start visibility time
				filtersQuery.inferiorOrEqualFields.push({
					fieldKey: "startVisibilityTime",
					compareData: DateTime.local().endOf("day").toISO()
				});

				// End visibility time
				filtersQuery.superiorOrEqualFields.push({
					fieldKey: "endVisibilityTime",
					compareData: DateTime.local().startOf("day").toISO()
				});
				// Filter on searchbar term if needed
				filtersQuery = this.buildSearchQueryFilters(filtersQuery);

				["asc", "desc"].includes(this.module.typeOrder)
					? filtersQuery.sortBy.push({
							fieldKey: "name",
							type: ["desc", "recent"].includes(this.module.typeOrder) ? "desc" : "asc"
						})
					: ["recent", "oldest"].includes(this.module.typeOrder)
						? (filtersQuery.sortBy = filtersQuery.sortBy.concat([
								{
									fieldKey: "creationDate",
									type: ["desc", "recent"].includes(this.module.typeOrder) ? "desc" : "asc"
								},
								{
									fieldKey: "name",
									type: "asc"
								}
							]))
						: filtersQuery.sortBy.push({
								fieldKey: "identifier",
								type: ["desc", "recent"].includes(this.module.typeOrder) ? "desc" : "asc"
							});

				this.filtersQuery.set({ ...filtersQuery });

				this.initDatasChecks.initFilters = true;
				this.initDatasSubject.next(this.initDatasChecks);

				this.getDatas();

				this.cdr.markForCheck();
			});
	}

	resetQueryFilters() {
		const filtersQuery = this.filtersQuery();
		filtersQuery.addFields = [];
		filtersQuery.arrayContainsAnyFields = [];
		filtersQuery.equalityFields = [
			{
				fieldKey: "eventId",
				compareData: this.eventId
			},
			{
				fieldKey: "moduleId",
				compareData: this.moduleId
			}
		];
		filtersQuery.includeTextFields = [];
		filtersQuery.includeOrTextFields = [];
		filtersQuery.inferiorFields = [];
		filtersQuery.inferiorOrEqualFields = [];
		filtersQuery.superiorFields = [];
		filtersQuery.superiorOrEqualFields = [];
		filtersQuery.arrayContainsBlocAndOrFields = [];
		filtersQuery.sortBy = [];
		filtersQuery.page = 0;

		// Check if there is custom fields of event users
		const cfFound = this.customFields.find(
			(custom) =>
				custom.baseSettings.type === TypeCustomFields.MODULE &&
				custom.baseSettings.customFieldModuleType === TypeModule.ATTENDEE
		);
		if (cfFound) {
			filtersQuery.lookup = [
				{
					where: "afterMatch",
					stage: {
						$lookup: {
							from: "event-users",
							let: {
								eventId: "$eventId",
								eventUsersIds: {
									$arrayElemAt: [
										{
											$filter: {
												input: "$customFields",
												as: "customField",
												cond: {
													$eq: ["$$customField.uid", cfFound.baseSettings.uid]
												},
												limit: 1
											}
										},
										0
									]
								}
							},
							pipeline: [
								{
									$match: {
										$expr: {
											$and: [
												{
													$eq: ["$$eventId", "$eventId"]
												},
												{
													$in: [["$uid"], ["$$eventUsersIds.field.module.items"]]
												}
											]
										}
									}
								},
								{
									$project: {
										email: 1,
										identifier: 1,
										name: 1,
										uid: 1
									}
								}
							],
							as: "cfsEventUsersDatas"
						}
					}
				}
			];
		}

		this.filtersQuery.set({ ...filtersQuery });
	}

	buildSearchQueryFilters(filtersQuery: ISearchFilter) {
		return buildFiltersQuery(
			[this.module],
			this.datasGetType,
			filtersQuery,
			this.searchValue,
			this.currentLanguage(),
			this.customFields,
			[],
			[],
			[],
			[],
			this.documents,
			this.groups,
			[],
			[]
		);
	}

	/**
	 * Reset filter
	 */
	resetFilter() {
		this.searchValue = "";
		this.buildFilterQuery();
		this.showNoResultImage = false;
		this.cdr.markForCheck();
	}

	/**
	 * getCustomFieldType
	 * @param customFieldId
	 * @returns
	 */
	getCustomFieldType(customFieldId: string): number {
		return this.customFields.find((cus) => cus.baseSettings.uid === customFieldId)?.baseSettings.type;
	}

	onLoadingMore: boolean = false;
	scrolledIndexChanged(index: number) {
		const nav = this.navigationSaved.find((navMod) => navMod.moduleId === this.moduleId);
		if (this.init) {
			if (!nav) {
				this.navigationSaved.push({
					moduleId: this.moduleId,
					lastIndex: index
				});
			} else {
				nav.lastIndex = index;
			}
		}

		if (
			this.virtualScroll.getRenderedRange().end === this.virtualScroll.getDataLength() &&
			this.datasGetType === "mongo" &&
			this.virtualScroll.getDataLength() < this.totalDatas &&
			!this.onLoadingMore
		) {
			this.onLoadingMore = true;
			this.moreDatas();
		}
	}

	resetPagination() {
		this.p = 0;
		if (this.virtualScroll) {
			this.virtualScroll.scrollToIndex(0);
		}
	}

	moreDatas() {
		if (this.datasGetType === "mongo") {
			this.p++;
			this.buildFilterQuery();
		}
	}

	getTotalFiltersChecked() {
		let count: number = 0;

		this.filters().locations.forEach((filter) => {
			if (filter.checked) {
				count++;
			}
		});
		this.filters().groups.forEach((filter) => {
			if (filter.checked) {
				count++;
			}
		});
		this.filters().tracks.forEach((filter) => {
			if (filter.checked) {
				count++;
			}
		});
		this.filters().customFields.forEach((filter) => {
			filter.values.forEach((value) => {
				if (value.isSelected) {
					count++;
				}
			});
		});

		return count;
	}

	/**
	 * Navigate to path
	 * @param path
	 */
	navigateTo(data: ISheet) {
		this.navCtrl.navigateForward(`/event/${data.eventId}/profile/${data.moduleId}/${data.uid}`);
	}
}
